<template>
  <div class="product-buy-box" :class="`product-buy-box_${variant}`">
    <div class="product-buy-box_head">{{ head }}</div>
    <div class="product-buy-box_body">
      <!--
        `_body` の先頭に入るブロック。支給の `set-discount.html:429-459` は
        `set-discount-products`（商品画像＋セット内訳）をここに置き、
        `.set-discount-products { padding: 20px 0 30px }`（`renewal/style.css:7858`）が
        その位置を前提にしている。使わないページでは何も出ない。
      -->
      <slot name="lead" />

      <h4 v-if="title" class="product-buy-box_title">{{ title }}</h4>

      <div v-if="tags.length" class="product-buy-box_tags">
        <span
          v-for="tag in tags"
          :key="tag.label"
          class="cart-tag"
          :class="`cart-tag_${tag.modifier}`"
        >
          {{ tag.label }}
        </span>
      </div>

      <!-- 定期枠は「通常価格→定期価格＋割引額」、通常枠は「単位＋価格」で構造が違う -->
      <div v-if="variant === 'regular'" class="product-buy-box_price">
        <p v-if="oldPrice !== null" class="product-buy-box_price-old">
          ［通常購入価格］<span>{{ formatYen(oldPrice) }}</span>
        </p>
        <div class="product-buy-box_price-current-wrap">
          <p class="product-buy-box_price-current">
            {{ price.toLocaleString() }}<span>円（税込）</span>
          </p>
          <p v-if="discount > 0" class="product-buy-box_price-off">
            {{ discount.toLocaleString() }}<span>{{ discountUnit }}</span>OFF!
          </p>
        </div>
      </div>
      <div v-else class="product-buy-box_price product-buy-box_price_normal">
        <span v-if="unitLabel" class="product-buy-box_price-unit">
          <span>{{ unitCount }}</span> {{ unitLabel }}あたり
        </span>
        <span class="product-buy-box_price-current">
          {{ price.toLocaleString() }}<span>円（税込）</span>
        </span>
      </div>

      <!-- 定期コース案内・電話案内など -->
      <slot />

      <!-- 横並びにできる幅を持つのはグレーのボタンだけ。緑は全幅ブロックなので入れると潰れる -->
      <div v-if="variant === 'normal' && buttonVariant === 'normal'" class="product-field-btn-wrap">
        <slot name="fields" />
        <button
          type="button"
          class="product-buy-btn"
          :class="`product-buy-btn_${buttonVariant}`"
          @click="$emit('submit')"
        >
          <img src="/image/common/header/icon_cart.png" alt="" class="product-buy-btn_icon" />
          {{ buttonLabel }}
        </button>
      </div>
      <template v-else>
        <slot name="fields" />
        <button
          type="button"
          class="product-buy-btn"
          :class="`product-buy-btn_${buttonVariant}`"
          @click="$emit('submit')"
        >
          <img src="/image/common/header/icon_cart.png" alt="" class="product-buy-btn_icon" />
          {{ buttonLabel }}
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `product-buy-box`（購入枠）。`_normal` 37/38（`NST` のみ無し）、`_regular` 21/38。
 * `/set`（セット割）は `_regular` を4個並べるので、**個数は呼び出し側で決める**（決定#53）。
 *
 * **`_regular` クラス＝定期とは限らない。** `QBM01` の「お得なセット割り」も `_regular` を使う
 * （決定#48-③）。枠の見出しは `head` で受け、支給の実測は「通常購入」37 ／「定期コース」20 ／
 * 「お得なセット割り」1。
 *
 * **価格の構造が2系統に分かれる。**
 * - `_regular` … `price-old`（［通常購入価格］）＋ `price-current` ＋ `price-off`（割引額）
 * - `_normal` … `price-unit`（「1 箱あたり」）＋ `price-current`。`price-old` と `price-off` は無い
 *
 * **`price-old` は商品詳細では出さない**（決定#88）。渡すのは `/cart/set` とマイページの
 * キャンペーンだけで、この2画面は本番にも取り消し線がある。OFF 額は商品詳細でも出す。
 *
 * **ボタンを数量セレクタと横並びにするかは、ボタンの色と連動する。** 支給38本を数えると
 * `_normal` 枠のうち **グレーのボタン20本はすべて `product-field-btn-wrap`（横並び）**で、
 * 支給CSS もグレーにだけ横に並べられる幅（`width: auto` / `min-width: 258px` / 高さ66px）を与えている。
 * 緑のボタンは `width: calc(100% - 20px)` / 高さ80px の**全幅ブロック**のままなので、
 * 横並びに入れると潰れる。支給は緑17本のうち **7本（`KF01` `KF02` `KF03` `KF05` `KF15` `KG01` `KG02`）で
 * 正しく枠を外している**が、残る10本（`L61` `L62` `QBD01` `QBD02` `QBM02` `K41`〜`K45`）は
 * 枠に入れたままで潰れる。**支給の不統一なので実装側で揃える**（決定#51）。
 *
 * **ボタンの色は「同じページに定期枠があるか」で決まる（決定#65）。** 支給38本を全数照合すると
 * `_normal` 枠37本（`NST` のみ枠が無い）のボタンが **グレー20件 ／ 緑17件**に割れており、
 * 緑の17件は**すべて `_regular` 枠を持たないページ**だった。定期枠と並ぶときは通常枠をグレーに
 * 落として定期を目立たせ、通常枠しか無いときは緑にする、という使い分けと読める。
 *
 * **判定は「API の `regularPrice` があるか」ではなく「そのページが `_regular` 枠を出すか」。**
 * `QBM01` は `regularPrice` が null（定期を持たない・決定#48-③）だが「お得なセット割り」を
 * `_regular` 枠で描くため、通常枠のボタンはグレーになる。呼び出し側は
 * `regularPrice !== null || セット割枠を出す` を `hasRegular` に渡す。
 *
 * これにより支給の不統一（`QBM02` は通常枠なのにボタン文言が「定期購入する」
 * ＝`productQBM02.html:480`。同商品は `regularPrice` が null）も併せて正す。
 *
 * 価格は数値で受け取り、表示のカンマ区切りはこの部品で行う（既存 `id/form.vue:23` と同じ `toLocaleString`）。
 * **ハードコードしない**（§3.7）。`discount` は「通常価格 − 定期価格」で呼び出し側が渡す。
 * → CONVERSION-GUIDE.md §3.3.1 の #5
 */
type Tag = {
  label: string;
  /** `cart-tag_regular` / `cart-tag_normal` / `cart-tag_shipping` */
  modifier: 'regular' | 'normal' | 'shipping';
};

type Props = {
  /** 枠の系統。`regular` は定期とセット割の両方で使う */
  variant: 'regular' | 'normal';
  /** 枠の見出し。「定期コース」「通常購入」「お得なセット割り」 */
  head: string;
  /** 枠内の商品名 */
  title: string;
  tags?: Tag[];
  /** 表示価格（税込・数値） */
  price: number;
  /** `_regular` のみ。［通常購入価格］に出す定価。無ければ `null`（商品詳細は渡さない・決定#88） */
  oldPrice?: number | null;
  /** `_regular` のみ。割引額。0 なら出さない */
  discount?: number;
  /**
   * 割引の単位。**支給21枠のうち `QBM01` のセット割だけが `10%OFF!` で、残る20枠は円**
   * （`productQBM01.html` の `product-buy-box_price-off`）。セット割は定価に対する率で出す。
   */
  discountUnit?: '円' | '%';
  /** `_normal` のみ。「1 箱あたり」の数値部分 */
  unitCount?: number;
  /**
   * `_normal` のみ。「箱」「袋」などの単位。**省略すると「1◯あたり」の行ごと出ない。**
   * 支給38本のうち `product-buy-box_price-unit` を持つのは21本で、KENCOS・KF・KG・QB 系の
   * 16本（`NST` を除く）は単価の行を持たず金額だけを出す。
   */
  unitLabel?: string;
  /** ボタンの文言。「定期購入する」「カートに入れる」「セット購入する」 */
  buttonLabel: string;
  /** 同じページに定期枠があるか。ボタンの色の判定に使う */
  hasRegular?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  tags: () => [],
  oldPrice: null,
  discount: 0,
  discountUnit: '円',
  unitCount: 1,
  unitLabel: '',
  hasRegular: false,
});

defineEmits<{ submit: [] }>();

const buttonVariant = computed<'regular' | 'normal'>(() => {
  if (props.variant === 'regular') return 'regular';
  return props.hasRegular ? 'normal' : 'regular';
});

const formatYen = (value: number | null): string =>
  value === null ? '' : `${value.toLocaleString()}円（税込）`;
</script>
